import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface AddInstallmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  contractId?: string;
}

interface Contract {
  id: string;
  contract_number: string;
  student_id: string;
  branch_id: string;
  total_amount: number;
  currency: string;
  students: {
    first_name: string;
    last_name: string;
  };
}

interface InstallmentData {
  installment_number: number;
  amount: string;
  due_date: string;
}

const AddInstallmentModal: React.FC<AddInstallmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  contractId
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selectedContractId, setSelectedContractId] = useState(contractId || '');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [installments, setInstallments] = useState<InstallmentData[]>([
    { installment_number: 1, amount: '', due_date: '' }
  ]);

  useEffect(() => {
    if (isOpen) {
      fetchContracts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (contractId) {
      setSelectedContractId(contractId);
    }
  }, [contractId]);

  useEffect(() => {
    if (selectedContractId) {
      const contract = contracts.find(c => c.id === selectedContractId);
      setSelectedContract(contract || null);

      if (contract && installments.length === 1 && !installments[0].amount) {
        setInstallments([{
          installment_number: 1,
          amount: contract.total_amount.toString(),
          due_date: ''
        }]);
      }
    } else {
      setSelectedContract(null);
    }
  }, [selectedContractId, contracts]);

  const fetchContracts = async () => {
    try {
      let query = supabase
        .from('contracts')
        .select(`
          id,
          contract_number,
          student_id,
          branch_id,
          total_amount,
          currency
        `)
        // .eq('status', 'active') // Show all contracts
        .order('created_at', { ascending: false });

      // Check if user has access to all branches
      const isAccounting = user?.role?.toLowerCase() === 'accounting' ||
        (user as any)?.unitType?.toLowerCase() === 'accounting' ||
        (user?.role?.toLowerCase() === 'unit' && (user as any)?.unitType?.toLowerCase() === 'accounting');
      const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'super_admin';

      if (user && !isAdmin && !isAccounting) {
        query = query.eq('branch_id', user.branchId);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rawContracts = data || [];
      const studentIds = [...new Set(rawContracts.map(c => c.student_id).filter(Boolean))];

      let studentsMap = new Map();
      if (studentIds.length > 0) {
        const { data: studentsData } = await supabase.from('students').select('id, first_name, last_name').in('id', studentIds);
        if (studentsData) {
          studentsMap = new Map(studentsData.map(s => [s.id, s]));
        }
      }

      const enrichedContracts = rawContracts.map(c => ({
        ...c,
        students: c.student_id ? studentsMap.get(c.student_id) || null : null
      }));

      setContracts(enrichedContracts as any);
    } catch (err) {
      console.error('Error fetching contracts:', err);
    }
  };

  const addInstallment = () => {
    const newNumber = installments.length + 1;
    setInstallments([
      ...installments,
      { installment_number: newNumber, amount: '', due_date: '' }
    ]);
  };

  const removeInstallment = (index: number) => {
    if (installments.length > 1) {
      const newInstallments = installments.filter((_, i) => i !== index);
      const renumbered = newInstallments.map((inst, idx) => ({
        ...inst,
        installment_number: idx + 1
      }));
      setInstallments(renumbered);
    }
  };

  const updateInstallment = (index: number, field: keyof InstallmentData, value: string) => {
    const updated = [...installments];
    updated[index] = { ...updated[index], [field]: value };
    setInstallments(updated);
  };

  const distributeAmount = () => {
    if (!selectedContract || installments.length === 0) return;

    const totalAmount = selectedContract.total_amount;
    const perInstallment = (totalAmount / installments.length).toFixed(2);

    const updated = installments.map((inst, index) => {
      if (index === installments.length - 1) {
        const sum = parseFloat(perInstallment) * (installments.length - 1);
        const lastAmount = (totalAmount - sum).toFixed(2);
        return { ...inst, amount: lastAmount };
      }
      return { ...inst, amount: perInstallment };
    });

    setInstallments(updated);
  };

  const generateMonthlyDates = () => {
    if (installments.length === 0) return;

    const today = new Date();
    const updated = installments.map((inst, index) => {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + index);
      return { ...inst, due_date: dueDate.toISOString().split('T')[0] };
    });

    setInstallments(updated);
  };

  const getTotalAmount = () => {
    return installments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedContractId) {
      alert('Lütfen bir sözleşme seçin');
      return;
    }

    const totalAmount = getTotalAmount();
    if (selectedContract && Math.abs(totalAmount - selectedContract.total_amount) > 0.01) {
      alert(
        `Taksit tutarları toplamı (${totalAmount.toFixed(2)}) sözleşme tutarına (${selectedContract.total_amount}) eşit olmalıdır`
      );
      return;
    }

    for (const inst of installments) {
      if (!inst.amount || parseFloat(inst.amount) <= 0) {
        alert('Tüm taksitlerin tutarı girilmelidir');
        return;
      }
      if (!inst.due_date) {
        alert('Tüm taksitlerin vade tarihi girilmelidir');
        return;
      }
    }

    setLoading(true);
    try {
      const installmentsData = installments.map(inst => ({
        contract_id: selectedContractId,
        student_id: selectedContract!.student_id,
        branch_id: selectedContract!.branch_id,
        installment_number: inst.installment_number,
        amount: parseFloat(inst.amount),
        currency: selectedContract!.currency,
        due_date: inst.due_date,
        status: 'pending'
      }));

      const { error } = await supabase
        .from('installments')
        .insert(installmentsData);

      if (error) throw error;

      alert('Taksitler başarıyla oluşturuldu');
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error creating installments:', err);
      alert(err.message || 'Taksitler oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedContractId(contractId || '');
    setSelectedContract(null);
    setInstallments([{ installment_number: 1, amount: '', due_date: '' }]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Taksit Planı Oluştur</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sözleşme Seçin *
            </label>
            <select
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
              disabled={!!contractId}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Sözleşme seçin...</option>
              {contracts.map((contract) => (
                <option key={contract.id} value={contract.id}>
                  {contract.contract_number} - {contract.students ? `${contract.students.first_name} ${contract.students.last_name}` : 'Bilinmeyen Öğrenci'}
                  ({contract.currency} {contract.total_amount.toLocaleString()})
                </option>
              ))}
            </select>
          </div>

          {selectedContract && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Öğrenci:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {selectedContract.students?.first_name} {selectedContract.students?.last_name}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Toplam Tutar:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {selectedContract.currency} {selectedContract.total_amount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {selectedContract && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Taksitler</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={distributeAmount}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    Eşit Dağıt
                  </button>
                  <button
                    type="button"
                    onClick={generateMonthlyDates}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium inline-flex items-center gap-1"
                  >
                    <Calendar className="w-4 h-4" />
                    Aylık Tarih
                  </button>
                  <button
                    type="button"
                    onClick={addInstallment}
                    className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium inline-flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Taksit Ekle
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {installments.map((installment, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-12 gap-3 items-start p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Taksit #
                      </label>
                      <input
                        type="number"
                        value={installment.installment_number}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div className="col-span-4">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Tutar ({selectedContract.currency}) *
                      </label>
                      <input
                        type="number"
                        value={installment.amount}
                        onChange={(e) => updateInstallment(index, 'amount', e.target.value)}
                        required
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="col-span-5">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Vade Tarihi *
                      </label>
                      <input
                        type="date"
                        value={installment.due_date}
                        onChange={(e) => updateInstallment(index, 'due_date', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="col-span-1 pt-6">
                      {installments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInstallment(index)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Toplam Taksit Tutarı:
                </span>
                <span className={`text-lg font-bold ${Math.abs(getTotalAmount() - selectedContract.total_amount) < 0.01
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
                  }`}>
                  {selectedContract.currency} {getTotalAmount().toFixed(2)}
                </span>
              </div>

              {Math.abs(getTotalAmount() - selectedContract.total_amount) > 0.01 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Uyarı: Taksit tutarları toplamı sözleşme tutarına eşit olmalıdır.
                    Fark: {Math.abs(getTotalAmount() - selectedContract.total_amount).toFixed(2)} {selectedContract.currency}
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="submit"
              disabled={loading || !selectedContract}
              className="flex-1 bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Oluşturuluyor...' : 'Taksitleri Oluştur'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-6 py-2.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddInstallmentModal;
